import * as React from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router";
import { StatusLabel } from "../../components/Lists/Base/CompleteList/styles";
import { DetailLayoutWrapper } from "../../components/sharedStyles";
import { constants, useApartment } from "../../store/apartments";
import DetailPageHeaderMenu from "../Layouts/DetailPageHeaderMenu/DetailPageHeaderMenu";

import ApartmentContracts from "./detail/Contracts";
import ApartmentImagesAndBlueprints from "./detail/ImagesAndBlueprint";
import ApartmentIMD from "./detail/IMD";
import ApartmentKeys from "./detail/Keys";
import ApartmentMarket from "./detail/Market";
import ApartmentOverview from "./detail/Overview";
import DeleteModal from "../../components/Forms/Delete/DeleteModal";
import { useAllPermissionCheck, usePermissionCheck } from "../../store/base";
import usePermissionRedirect from "../../hooks/usePermissionRedirect";
import { useExternalSyncErrorPaginationCount } from "../../store/externalSyncErrors";
import { SYNC_ERROR_MAP } from "../../components/Tables/ExternalSyncErrors/FullTable";
import ObjectCosts from "../../components/Details/OverviewInfo/Costs/ObjectCosts";
import TFPremisesBase from "../../components/TechnicalManagement/PremisesBase";

export default function MainApartmentDetail() {
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);

  const { path, url } = useRouteMatch();
  const { push } = useHistory();
  const { apartmentId } = useParams();
  usePermissionRedirect(["view_can_baseobject"]);

  const canDelete = usePermissionCheck("delete_can_baseobject");

  const canErrands = useAllPermissionCheck([
    "allow_errand_faults",
    "view_can_reporterrand",
  ]);
  const canMarket = useAllPermissionCheck(["allow_market", "view_can_market"]);
  const canImd = useAllPermissionCheck(["allow_imd", "view_can_imd"]);

  const canKeys = useAllPermissionCheck(["allow_keys", "view_can_keys"]);

  const [apartment] = useApartment(apartmentId);

  const [marketErrorCount] = useExternalSyncErrorPaginationCount({
    filters: {
      api_kind__in: [SYNC_ERROR_MAP.HOMEQ, SYNC_ERROR_MAP.SBF],
      apartment: apartmentId,
    },
  });

  const hasMarketError = marketErrorCount > 0;

  const SUB_PAGES = [
    {
      path: "/overview",
      label: "Objekt",
      component: ApartmentOverview,
    },
    {
      path: "/costs",
      label: "Debiteringsrader",
      component: ObjectCosts,
    },
    {
      path: "/images-and-blueprint",
      label: "Bilder & ritning",
      component: ApartmentImagesAndBlueprints,
    },
    {
      path: "/contracts",
      label: "Avtal",
      component: ApartmentContracts,
    },
  ];

  if (canErrands) {
    SUB_PAGES.push({
      path: "/errands",
      label: "Teknisk förvaltning",
      component: TFPremisesBase,
    });
  }

  if (canMarket) {
    SUB_PAGES.push({
      path: "/market",
      label: "Marknad",
      component: ApartmentMarket,
      hasError: hasMarketError,
    });
  }

  if (canImd) {
    SUB_PAGES.push({
      path: "/imd",
      label: "IMD",
      component: ApartmentIMD,
    });
  }

  if (canKeys) {
    SUB_PAGES.push({
      path: "/keys",
      label: "Nycklar",
      component: ApartmentKeys,
    });
  }

  const subPageLinks = SUB_PAGES.map((sp) => ({
    url: `${url}${sp.path}`,
    label: sp.label,
    hasError: sp.hasError,
  }));

  const handleActions = [];

  if (canDelete) {
    handleActions.push({
      name: "Radera",
      onClick: () => setDeleteModalOpen(true),
      isDelete: true,
    });
  }

  const renderInfoPills = () => {
    return (
      <>
        <StatusLabel contentType="premises" state={apartment?.state}>
          {apartment?.state_display}
          {apartment?.subletted ? " (andrahand)" : ""}
        </StatusLabel>
      </>
    );
  };

  return (
    <>
      {canDelete && (
        <DeleteModal
          isOpen={deleteModalOpen}
          instance={apartment}
          closeFunction={() => setDeleteModalOpen(false)}
          constants={constants}
          deletedCallback={() => push("/apartments")}
        />
      )}

      <DetailLayoutWrapper>
        <DetailPageHeaderMenu
          title={`Hyreslägenhet ${
            apartment?.str_representation || "laddar..."
          }`}
          breadCrumbs={[
            { url: "/apartments", label: "Hyreslägenheter" },
            { label: apartment?.str_representation || "Laddar..." },
          ]}
          eventContentType="standard.apartment"
          eventObjectId={apartmentId}
          eventIdQueryStr="apartment"
          {...{ subPages: subPageLinks, handleActions, renderInfoPills }}
        />

        <Switch>
          {SUB_PAGES.map((sp) => (
            <Route
              key={sp.path}
              path={`${path}${sp.path}`}
              component={sp.component}
            />
          ))}

          {/* Default sub page  */}
          <Redirect exact to={`${url}${SUB_PAGES[0].path}`} path={`${path}`} />
        </Switch>
      </DetailLayoutWrapper>
    </>
  );
}
